/**
 * Hook to get industry labels based on current user role and company
 */
import { useSettingsContext } from '@/contexts/SettingsContext';
import { getLabelForUser, getIconForUser } from '@/lib/labels';
import { useMemo } from 'react';

export function useLabels() {
  const { labels = [], is_superadmin = false, memberships = [], companies = [] } = useSettingsContext();

  // Get the user's role (superadmin, admin, or user)
  const userRole = useMemo(() => {
    if (is_superadmin) return 'superadmin';
    // Check if user has admin role in any membership
    const hasAdminRole = memberships.some(m => m.role === 'admin' || m.role === 'group_admin');
    return hasAdminRole ? 'admin' : 'user';
  }, [is_superadmin, memberships]);

  // Get the primary company's industry (default to 'default' if not found)
  const companyIndustry = useMemo(() => {
    // Try to get industry from the first company the user has access to
    const firstCompany = companies.find(acc =>
      memberships.some(m => m.company_id === acc.id)
    );
    // Use company's industry if available, otherwise default to 'default'
    return firstCompany?.industry || 'default';
  }, [companies, memberships]);

  const getLabel = (labelType: 'group' | 'unit' | 'site' | 'team'): string => {
    return getLabelForUser(labelType, userRole, companyIndustry, labels);
  };

  const getIcon = (labelType: 'group' | 'unit' | 'site' | 'team'): string | null => {
    return getIconForUser(labelType, userRole, companyIndustry, labels);
  };

  return {
    getLabel,
    getIcon,
    labels,
    userRole,
    companyIndustry,
  };
}

